import Swiper from "./swiper-bundle.esm.browser.min.js";

const MOBILE_BREAKPOINT = 769;

class TheGutInfluences extends HTMLElement {
  constructor() {
    super();
    this._swiper = null;
    this._resizeTimer = null;
    this._initialized = false;
    this._handleResize = this._handleResize.bind(this);
    this._handleSectionLoad = this._handleSectionLoad.bind(this);
    this._handleSectionUnload = this._handleSectionUnload.bind(this);
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;
    document.addEventListener("shopify:section:load", this._handleSectionLoad);
    document.addEventListener("shopify:section:unload", this._handleSectionUnload);
    window.addEventListener("resize", this._handleResize, { passive: true });
    this._updateLayout();
  }

  disconnectedCallback() {
    this._initialized = false;
    this._destroySwiper();
    window.removeEventListener("resize", this._handleResize);
    document.removeEventListener("shopify:section:load", this._handleSectionLoad);
    document.removeEventListener("shopify:section:unload", this._handleSectionUnload);
    if (this._resizeTimer) {
      clearTimeout(this._resizeTimer);
      this._resizeTimer = null;
    }
  }

  _isOurSection(event) {
    const section = this.closest(".shopify-section");
    return section && (event.target === section || section.contains(event.target));
  }

  _handleSectionLoad(event) {
    if (!this._isOurSection(event)) return;
    this._destroySwiper();
    this._updateLayout();
  }

  _handleSectionUnload(event) {
    if (!this._isOurSection(event)) return;
    this._destroySwiper();
  }

  _readConfig() {
    const cardCount = parseInt(this.dataset.cardCount || "0", 10);
    return {
      cardCount,
      sectionId: this.dataset.sectionId || "",
      spaceBetween: parseInt(this.dataset.spaceBetween || "20", 10),
      mobileSlidesPerView: parseFloat(this.dataset.slidesPerViewMobile || "1.2"),
      desktopSlidesPerView: parseFloat(this.dataset.slidesPerViewDesktop || "2"),
      staticThreshold: parseInt(this.dataset.staticThreshold || "2", 10),
      showArrows: this.dataset.showArrows === "true",
      showPagination: this.dataset.showPagination === "true",
      enableLoop: this.dataset.enableLoop === "true",
      enableAutoplay: this.dataset.enableAutoplay === "true",
      autoplaySpeed: parseInt(this.dataset.autoplaySpeed || "5000", 10),
      pauseOnHover: this.dataset.pauseOnHover !== "false",
      pauseOnInteraction: this.dataset.pauseOnInteraction === "true",
    };
  }

  _isMobile() {
    return window.innerWidth < MOBILE_BREAKPOINT;
  }

  _shouldUseSwiper(config) {
    if (config.cardCount <= 1) return false;
    return config.cardCount > config.staticThreshold;
  }

  _getSlidesPerView(config) {
    const requested = this._isMobile()
      ? config.mobileSlidesPerView
      : config.desktopSlidesPerView;
    return Math.min(requested, config.cardCount);
  }

  _cleanupSwiperClasses() {
    const swiperEl = this.querySelector("[data-swiper]");
    const container = this.querySelector("[data-swiper-container]");
    const slides = this.querySelectorAll("[data-swiper-slide]");

    if (swiperEl) {
      swiperEl.classList.remove("swiper", "tgi__swiper", "swiper-initialized");
    }
    if (container) {
      container.classList.remove("swiper-wrapper");
    }
    slides.forEach((slide) => slide.classList.remove("swiper-slide"));
  }

  _applyStaticMode(config) {
    this.classList.add("tgi--static");
    this.classList.remove("tgi--swiper");
    this._cleanupSwiperClasses();

    const columns = this._isMobile()
      ? 1
      : Math.min(config.cardCount, Math.floor(config.desktopSlidesPerView));

    this.style.setProperty("--tgi-static-columns", String(Math.max(columns, 1)));
    this._syncSlideAccessibility(0);
  }

  _prepareSwiperStructure() {
    const swiperEl = this.querySelector("[data-swiper]");
    const container = this.querySelector("[data-swiper-container]");
    const slideEls = this.querySelectorAll("[data-swiper-slide]");

    if (!swiperEl || !container || slideEls.length === 0) return null;

    swiperEl.classList.add("swiper", "tgi__swiper");
    container.classList.add("swiper-wrapper");
    slideEls.forEach((slide) => slide.classList.add("swiper-slide"));

    return { slideEls };
  }

  _syncSlideAccessibility(activeIndex) {
    const slides = this.querySelectorAll("[data-swiper-slide]");
    const isStatic = this.classList.contains("tgi--static");

    slides.forEach((slide, index) => {
      if (isStatic) {
        slide.setAttribute("aria-hidden", "false");
        slide.removeAttribute("tabindex");
        return;
      }

      const isActive = index === activeIndex;
      slide.setAttribute("aria-hidden", isActive ? "false" : "true");
      slide.setAttribute("tabindex", isActive ? "0" : "-1");
    });
  }

  _initSwiper(config) {
    const structure = this._prepareSwiperStructure();
    if (!structure) return;

    const { slideEls } = structure;
    const slidesPerView = this._getSlidesPerView(config);
    const canNavigate = slideEls.length > slidesPerView;
    const canLoop =
      config.enableLoop && canNavigate && slideEls.length > slidesPerView;

    this.classList.add("tgi--swiper");
    this.classList.remove("tgi--static");

    const swiperConfig = {
      slidesPerView: Math.min(config.mobileSlidesPerView, config.cardCount),
      slidesPerGroup: 1,
      spaceBetween: config.spaceBetween,
      watchOverflow: true,
      grabCursor: canNavigate || slideEls.length > 1,
      speed: 400,
      keyboard: {
        enabled: true,
        onlyInViewport: true,
      },
      a11y: {
        enabled: true,
        prevSlideMessage: "Previous card",
        nextSlideMessage: "Next card",
      },
      breakpoints: {
        [MOBILE_BREAKPOINT]: {
          slidesPerView: Math.min(config.desktopSlidesPerView, config.cardCount),
          spaceBetween: config.spaceBetween,
        },
      },
    };

    if (canLoop) {
      swiperConfig.loop = true;
    }

    if (config.showArrows && slideEls.length > 1) {
      const nextEl = this.querySelector(`.tgi__nav-next--${config.sectionId}`);
      const prevEl = this.querySelector(`.tgi__nav-prev--${config.sectionId}`);
      if (nextEl && prevEl) {
        swiperConfig.navigation = { nextEl, prevEl };
      }
    }

    if (config.showPagination && slideEls.length > 1) {
      const paginationEl = this.querySelector(
        `.tgi__pagination--${config.sectionId}`,
      );
      if (paginationEl) {
        swiperConfig.pagination = {
          el: paginationEl,
          clickable: true,
          renderBullet: (index, className) => {
            const num = index + 1;
            const total = config.cardCount;
            return `<button type="button" class="${className}" aria-label="Go to card ${num} of ${total}"></button>`;
          },
        };
      }
    }

    if (config.enableAutoplay && canNavigate) {
      swiperConfig.autoplay = {
        delay: config.autoplaySpeed,
        disableOnInteraction: config.pauseOnInteraction,
        pauseOnMouseEnter: config.pauseOnHover,
      };
    }

    const existingOn = swiperConfig.on || {};
    swiperConfig.on = {
      ...existingOn,
      afterInit: (swiper) => {
        existingOn.afterInit?.(swiper);
        this._syncSlideAccessibility(swiper.realIndex ?? swiper.activeIndex);
      },
      slideChangeTransitionEnd: (swiper) => {
        existingOn.slideChangeTransitionEnd?.(swiper);
        this._syncSlideAccessibility(swiper.realIndex ?? swiper.activeIndex);
      },
      resize: (swiper) => {
        existingOn.resize?.(swiper);
        swiper.update();
      },
    };

    const swiperEl = this.querySelector("[data-swiper]");
    this._swiper = new Swiper(swiperEl, swiperConfig);
    requestAnimationFrame(() => this._swiper?.update());
  }

  _destroySwiper() {
    if (this._swiper && !this._swiper.destroyed) {
      this._swiper.destroy(true, true);
    }
    this._swiper = null;
    this._cleanupSwiperClasses();
  }

  _updateLayout() {
    const config = this._readConfig();
    const shouldUseSwiper = this._shouldUseSwiper(config);
    const isSwiperActive = Boolean(this._swiper);

    if (!shouldUseSwiper) {
      this._destroySwiper();
      this._applyStaticMode(config);
      return;
    }

    if (isSwiperActive) {
      this._destroySwiper();
    }

    this._initSwiper(config);
  }

  _handleResize() {
    if (this._resizeTimer) clearTimeout(this._resizeTimer);
    this._resizeTimer = setTimeout(() => {
      const config = this._readConfig();
      const shouldUseSwiper = this._shouldUseSwiper(config);
      const isSwiperActive = Boolean(this._swiper);

      if (shouldUseSwiper !== isSwiperActive) {
        this._updateLayout();
        return;
      }

      if (this._swiper && !this._swiper.destroyed) {
        this._swiper.update();
      } else if (this.classList.contains("tgi--static")) {
        this._applyStaticMode(config);
      }
    }, 150);
  }
}

if (!customElements.get("the-gut-influences")) {
  customElements.define("the-gut-influences", TheGutInfluences);
}
